import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Stack, Typography } from '@mui/material_modern'
import { useGetCustomerQuery } from 'services/customers'
import InputV2 from 'components/generic/form/InputV2'
import Select from 'components/generic/form/Select'
import RadioGroup from 'components/generic/form/RadioGroup'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import { getStatesAndProvinces, isLocationInYardList } from 'utils/helpers'
import { objectDetailsToDropdownFormatter } from 'utils/data-manipulation/common'
import { useSelector } from 'react-redux'

const TrailerProperties = ({ methods, assetData, assetTypesData, canEditLocation }) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
  } = methods

  const statesAndProvinces = useMemo(() => getStatesAndProvinces(['United States', 'Canada']), [])
  const [customerId, setCustomerId] = useState('')
  const [locationOptions, setLocationOptions] = useState([])
  // StoredYardLocationId allows us to change the locationId in the form without losing the original selected yard
  const [storedYardLocationId, setStoredYardLocationId] = useState('')
  const selectedLocationType = watch('locationType')
  const selectedCustomer = watch('customer')
  const locationId = watch('locationId')

  const { locationId: yardLocationId } = useSelector(state => state.settings.details)
  const { inOrgMode } = useSelector(state => state.ui)
  const { yardDetailsList, yardId } = useSelector(state => state.auth)

  const { data: customerData } = useGetCustomerQuery(
    { customerId },
    { refetchOnMountOrArgChange: true, skip: !customerId },
  )

  const yardSelected = useMemo(() => isLocationInYardList(locationId, yardDetailsList), [yardDetailsList, locationId])

  const yardOptions = useMemo(() => {
    return (
      yardDetailsList?.map((item, index) => objectDetailsToDropdownFormatter(item, index, { idKey: 'locationId' })) ||
      []
    )
  }, [yardDetailsList])

  const setLocation = (newLocationId, shouldSaveYardLocation = false, shouldDirty = true) => {
    setValue('locationId', newLocationId || '', { shouldDirty })

    // This updates our local variable for ui purposes
    if (shouldSaveYardLocation) {
      setStoredYardLocationId(newLocationId || '')
    }
  }

  const setCustomerValue = (customer, asset, shouldDirty = true) => {
    setValue('customer', customer || null, { shouldDirty })
    setLocationOptions(customer?.locations || [])
    setLocation(
      customer?.locations?.find(location => location.id === asset?.assetStatus?.currentLocation?.id)?.id ||
        customer?.locations?.[0]?.id ||
        {},
      shouldDirty,
    )
  }

  useEffect(() => {
    if (assetData) {
      setCustomerId(assetData?.assetStatus?.currentLocation?.customerId)

      if (inOrgMode) {
        // For assets with yard location selected
        if (
          isLocationInYardList(assetData?.assetStatus?.currentLocation?.id, yardDetailsList) &&
          selectedLocationType === 'yard-location'
        ) {
          setStoredYardLocationId(assetData?.assetStatus?.currentLocation?.id)
        } else if (yardDetailsList?.length > 0) {
          // For assets with customer location selected
          setStoredYardLocationId(yardDetailsList[0]?.locationId)
        }
      }
    } else if (yardDetailsList?.length > 0) {
      // For new assets with no assetData prop
      setLocation(yardDetailsList[0]?.locationId, true)
    }
  }, [assetData])

  useEffect(() => {
    if (customerData === undefined) return
    setCustomerValue(customerData, assetData, false)
  }, [customerData, assetData])

  return (
    <>
      <Stack key="trailer-properties" flexGrow={1} ml={4} spacing={2}>
        <Typography variant="h6">Trailer Properties</Typography>
        <Stack direction="row" spacing={1} width={{ xs: '100%', md: '50%' }}>
          <InputV2
            name="assetName"
            label="Trailer Name"
            data-test="trailer-asset-name"
            size="small"
            defaultValue=""
            control={control}
            error={!!errors?.assetName}
            helperText={errors?.assetName?.message}
            onChange={() => clearErrors('assetName')}
          />
          <Box />
        </Stack>
        <Stack direction="row" spacing={1} width={{ xs: '100%', md: '50%' }}>
          <Select
            name="assetTypeId"
            label="Trailer Type"
            control={control}
            error={!!errors?.assetTypeId}
            variant="outlined"
            size="small"
            data-testid="asset-type-id"
            defaultValue={assetTypesData?.[0]?.id || ''}
            items={assetTypesData || []}
            helperText={errors?.assetTypeId?.message}
          />
          <Box />
        </Stack>
        <Stack direction="row" spacing={2} width={{ xs: '100%' }}>
          <InputV2
            defaultValue=""
            name="plateNumber"
            label="License Plate Number"
            data-test="trailer-asset-plate-number"
            size="small"
            control={control}
            error={errors?.plateNumber}
          />
          <Select
            name="plateState"
            label="License Plate State"
            defaultValue=""
            control={control}
            size="small"
            items={statesAndProvinces}
            error={errors?.plateState}
          />
        </Stack>
        <Stack direction="row" spacing={1} width={{ xs: '100%' }}>
          <InputV2
            defaultValue=""
            name="notes"
            label="Notes"
            data-test="trailer-asset-notes"
            size="small"
            control={control}
            error={errors?.notes}
          />
        </Stack>
      </Stack>
      <Stack key="trailer-locations" flexGrow={1} ml={4} mt={3} spacing={2}>
        <Typography variant="h6">Trailer Locations</Typography>
        <Box pl={1} width={{ xs: '100%', md: '50%' }}>
          <RadioGroup
            name="locationType"
            label=""
            control={control}
            onChange={({ target: { value } }) => {
              // When switching between yard and customer location radios, the location
              // and customer need to be set to null
              setValue('customer', null, { shouldDirty: true })

              if (value === 'yard-location') {
                setLocation(storedYardLocationId)
                setLocationOptions([])
                if (!inOrgMode) {
                  setLocation(yardLocationId)
                }
              } else {
                setLocation(null)
              }
            }}
            defaultValue="yard-location"
            disabled={!canEditLocation}
            items={[
              {
                id: 'yard-location',
                name: 'In Yard',
              },
              {
                id: 'customer-location',
                name: 'Customer Location',
              },
            ]}
          />
        </Box>
        {selectedLocationType === 'yard-location' && inOrgMode && (
          <Box width={{ xs: '100%', md: '50%' }}>
            <Select
              name="locationId"
              label="Yard"
              control={control}
              variant="outlined"
              size="small"
              selectProps={{
                onChange: ({ target: { value } }) => {
                  setLocation(value, true)
                },
              }}
              items={yardOptions}
              helperText={errors?.yardId?.message || (yardSelected ? '' : 'Yard needs to be selected')}
              defaultValue={inOrgMode ? '' : yardId}
              disabled={!canEditLocation || !inOrgMode}
            />
          </Box>
        )}
        {selectedLocationType === 'customer-location' && (
          <Box width={{ xs: '100%', md: '50%' }}>
            <CustomerAutocomplete
              name="customer"
              label="Customer Name"
              variant="outlined"
              size="small"
              defaultValue={null}
              freeSolo={false}
              onChange={customer => setCustomerValue(customer)}
              control={control}
              autocompleteProps={{
                renderOption: (props, option) => (
                  <li {...props} key={option.id}>
                    {option.displayName}
                  </li>
                ),
              }}
              disabled={!canEditLocation || selectedLocationType !== 'customer-location'}
            />
          </Box>
        )}
        {selectedLocationType === 'customer-location' && (
          <Box width={{ xs: '100%', md: '50%' }}>
            <Select
              name="locationId"
              label="Location"
              control={control}
              variant="outlined"
              size="small"
              selectProps={{
                onChange: ({ target: { value } }) => {
                  setLocation(value)
                },
              }}
              items={locationOptions}
              helperText={
                errors?.locationId?.message ||
                (selectedLocationType === 'customer-location' && !selectedCustomer
                  ? 'Customer needs to be selected'
                  : '')
              }
              defaultValue=""
              disabled={!canEditLocation || selectedLocationType !== 'customer-location' || !selectedCustomer}
            />
          </Box>
        )}
      </Stack>
    </>
  )
}

TrailerProperties.propTypes = {
  methods: PropTypes.object,
  assetData: PropTypes.object,
  assetTypesData: PropTypes.array,
  canEditLocation: PropTypes.bool,
}

TrailerProperties.defaultProps = {
  methods: {},
  assetData: null,
  assetTypesData: [],
  canEditLocation: false,
}

export default TrailerProperties
